import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

/**
 * Two-player rummy, cut down to what a nine-year-old can hold in their head:
 * a short deck, seven cards, and one goal — two groups of three.
 *
 * A group is three of the same number, or three in a row of the same suit.
 * That is the entire rule set, which is the point: real rummy's scoring,
 * laying off and knocking are where children lose the thread.
 */

export const RUMMY_SUITS = ["♥️", "♠️", "♦️", "♣️"] as const;
/** Ranks 1–10 in each suit: forty cards, so runs come up often enough to hunt. */
export const RUMMY_RANKS = 10;
export const RUMMY_HAND = 7;
export const RUMMY_MELD = 3;
/** Groups needed to go out. Seven cards is two groups and one spare. */
export const RUMMY_GOAL = 2;
/**
 * Turns before the game is settled on who is closest. Taking the face-up card
 * and immediately throwing it back leaves everything exactly as it was and
 * never touches the pile, so the stock running out is not on its own enough to
 * guarantee an ending.
 */
export const RUMMY_MOVE_LIMIT = 60;

export const suitOf = (card: number) => Math.floor(card / RUMMY_RANKS);
export const rankOf = (card: number) => (card % RUMMY_RANKS) + 1;

export type RummyPhase = "draw" | "discard";

export interface RummyState extends BaseState {
	kind: "rummy";
	hands: [number[], number[]];
	/** Face down. Redacted to its length for both players. */
	stock: number[];
	/** Face up, newest last. */
	discard: number[];
	phase: RummyPhase;
	movesMade: number;
	/** Groups each player is holding, so the board can show progress. */
	groups: [number, number];
	/** What just happened, for the running commentary. */
	lastNote: string;
}

export type RummyMove = { take: "stock" | "discard" } | { drop: number };

/** Three of a kind, or three in a row in one suit. */
export function isGroup(cards: readonly number[]): boolean {
	if (cards.length !== RUMMY_MELD) return false;
	const ranks = cards.map(rankOf).sort((a, b) => a - b);
	if (ranks[0] === ranks[1] && ranks[1] === ranks[2]) return true;

	const suits = cards.map(suitOf);
	if (suits[0] !== suits[1] || suits[1] !== suits[2]) return false;
	return ranks[1] === ranks[0] + 1 && ranks[2] === ranks[1] + 1;
}

/**
 * The most groups that can be picked out of a hand at once, without using a
 * card twice. Seven cards is only a hundred-odd combinations, so this simply
 * tries them all rather than reasoning cleverly and getting it subtly wrong.
 */
export function countGroups(hand: readonly number[]): number {
	const best = (remaining: readonly number[]): number => {
		let most = 0;
		for (let a = 0; a < remaining.length; a++) {
			for (let b = a + 1; b < remaining.length; b++) {
				for (let c = b + 1; c < remaining.length; c++) {
					const group = [remaining[a], remaining[b], remaining[c]];
					if (!isGroup(group)) continue;
					const rest = remaining.filter((_, index) => index !== a && index !== b && index !== c);
					most = Math.max(most, 1 + best(rest));
				}
			}
		}
		return most;
	};
	return best(hand);
}

function freshDeck(rng: Rng): number[] {
	const deck: number[] = [];
	for (let card = 0; card < RUMMY_SUITS.length * RUMMY_RANKS; card++) deck.push(card);
	return rng.shuffle(deck);
}

/** Nobody went out, so whoever has more groups — then more cards — takes it. */
function settleOnGroups(state: RummyState): RummyState {
	const groups: [number, number] = [countGroups(state.hands[0]), countGroups(state.hands[1])];
	const winner = groups[0] === groups[1] ? null : groups[0] > groups[1] ? 0 : 1;
	return {
		...state,
		groups,
		scores: groups,
		turn: null,
		result: { winner },
		lastNote: "The pile ran out.",
	};
}

export const rummyRush: GameDefinition<RummyState, RummyMove> = {
	id: "rummy",
	category: "cards",
	title: "Rummy Rush",
	emoji: "🎴",
	blurb: "Collect two groups of three — same number, or three in a row.",
	howTo: [
		"Take the top card from the pile, or the face-up one, then throw one away.",
		"A group is three of the same number, or three in a row in one suit.",
		"First to hold two groups at once wins.",
	],

	create(rng) {
		const deck = freshDeck(rng);
		const hands: [number[], number[]] = [
			deck.slice(0, RUMMY_HAND),
			deck.slice(RUMMY_HAND, RUMMY_HAND * 2),
		];
		const rest = deck.slice(RUMMY_HAND * 2);
		return {
			kind: "rummy",
			hands,
			stock: rest.slice(1),
			discard: [rest[0]],
			phase: "draw",
			movesMade: 0,
			groups: [countGroups(hands[0]), countGroups(hands[1])],
			lastNote: "Take a card to start.",
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const value = raw as { take?: unknown; drop?: unknown };
		if (value.take === "stock" || value.take === "discard") return { take: value.take };
		if (typeof value.drop === "number" && Number.isInteger(value.drop) && value.drop >= 0) {
			return { drop: value.drop };
		}
		return null;
	},

	apply(state, player, move): MoveOutcome<RummyState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const hands: [number[], number[]] = [[...state.hands[0]], [...state.hands[1]]];

		if ("take" in move) {
			if (state.phase !== "draw") return reject("Throw a card away first.");

			if (move.take === "discard") {
				const card = state.discard[state.discard.length - 1];
				if (card === undefined) return reject("There's nothing face up to take.");
				hands[player].push(card);
				return {
					ok: true,
					state: {
						...state,
						hands,
						discard: state.discard.slice(0, -1),
						phase: "discard",
						lastNote: "Took the face-up card.",
					},
				};
			}

			if (state.stock.length === 0) return { ok: true, state: settleOnGroups(state) };
			hands[player].push(state.stock[0]);
			return {
				ok: true,
				state: {
					...state,
					hands,
					stock: state.stock.slice(1),
					phase: "discard",
					lastNote: "Took a card from the pile.",
				},
			};
		}

		if (state.phase !== "discard") return reject("Take a card first.");
		if (move.drop >= hands[player].length) return reject("You don't have that card.");

		const [thrown] = hands[player].splice(move.drop, 1);
		const groups: [number, number] = [countGroups(hands[0]), countGroups(hands[1])];
		const next: RummyState = {
			...state,
			hands,
			discard: [...state.discard, thrown],
			phase: "draw",
			groups,
			scores: groups,
			lastNote: "Threw a card away.",
			movesMade: state.movesMade + 1,
		};

		if (groups[player] >= RUMMY_GOAL) {
			return {
				ok: true,
				state: { ...next, turn: null, result: { winner: player }, lastNote: "Two groups!" },
			};
		}
		// Running the pile down has to end the game, or it would go round forever.
		if (next.stock.length === 0) return { ok: true, state: settleOnGroups(next) };
		if (next.movesMade >= RUMMY_MOVE_LIMIT) return { ok: true, state: settleOnGroups(next) };

		return { ok: true, state: { ...next, turn: otherPlayer(player) } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		if (state.phase === "draw") {
			const moves: RummyMove[] = [];
			if (state.stock.length > 0) moves.push({ take: "stock" });
			if (state.discard.length > 0) moves.push({ take: "discard" });
			return moves;
		}
		return state.hands[player].map((_, index) => ({ drop: index }));
	},

	/** Your own cards and the count of theirs. The pile stays face down. */
	redact(state, viewer) {
		const hidden = otherPlayer(viewer);
		const hands: [number[], number[]] = [[...state.hands[0]], [...state.hands[1]]];
		hands[hidden] = hands[hidden].map(() => -1);
		return { ...state, hands, stock: state.stock.map(() => -1) };
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/**
 * Takes the face-up card when it helps, and throws away whatever contributes
 * least. "Least" is measured by removing each card in turn and seeing what the
 * rest is worth — the same way a person decides, rather than a table of values.
 */
export function bestRummyMove(state: RummyState, player: PlayerIndex): RummyMove | null {
	const hand = state.hands[player];

	if (state.phase === "draw") {
		const faceUp = state.discard[state.discard.length - 1];
		if (faceUp !== undefined) {
			const withIt = countGroups([...hand, faceUp]);
			// Only worth taking if it actually builds something, since taking it
			// tells the other player exactly what you are collecting.
			if (withIt > countGroups(hand)) return { take: "discard" };
		}
		return state.stock.length > 0 ? { take: "stock" } : { take: "discard" };
	}

	let bestIndex = 0;
	let bestScore = Number.NEGATIVE_INFINITY;
	for (let index = 0; index < hand.length; index++) {
		const rest = hand.filter((_, i) => i !== index);
		// Groups first; then near-misses, so pairs and part-runs are kept.
		const score = countGroups(rest) * 100 + partials(rest);
		if (score > bestScore) {
			bestScore = score;
			bestIndex = index;
		}
	}
	return { drop: bestIndex };
}

/** Pairs and two-card runs — the beginnings of a group, worth hanging on to. */
function partials(hand: readonly number[]): number {
	let score = 0;
	for (let a = 0; a < hand.length; a++) {
		for (let b = a + 1; b < hand.length; b++) {
			if (rankOf(hand[a]) === rankOf(hand[b])) score += 1;
			else if (
				suitOf(hand[a]) === suitOf(hand[b]) &&
				Math.abs(rankOf(hand[a]) - rankOf(hand[b])) === 1
			) {
				score += 1;
			}
		}
	}
	return score;
}
